// Cattura uno "snapshot diagnostico" del client da allegare a un ticket di
// supporto. Niente dati sensibili: solo metadata utili per debug.
//
// Usato da SupportTicketModal al primo invio. Il payload finisce in
// support_tickets.debug_snapshot (jsonb), visibile solo al super_admin
// nel pannello support.
import { supabaseV2 } from "./supabase";
import i18n from "./i18n";
import { getConsoleBuffer, getNetworkBuffer } from "./debug-buffer";

export type DebugSnapshot = {
  collected_at: string;
  app: {
    build_hash: string | null;
    build_time: string | null;
    mode: string | null;
  };
  user: {
    id: string | null;
    email: string | null;
    lang: string;
    meta_keys: string[];
  };
  team: {
    active_id: string | null;
  };
  view: {
    pathname: string;
    href: string;
    referrer: string | null;
  };
  browser: {
    user_agent: string;
    language: string | undefined;
    languages: readonly string[] | undefined;
    platform: string | undefined;
    viewport: string;
    screen: string;
    device_pixel_ratio: number;
    online: boolean;
    cookies_enabled: boolean;
    color_scheme: "light" | "dark" | "unknown";
    reduced_motion: boolean;
  };
  storage: {
    localstorage_keys: string[];
    localstorage_size_kb: number;
    sessionstorage_keys: string[];
  };
  push: {
    permission: string;
  };
  console_tail: ReturnType<typeof getConsoleBuffer>;
  network_tail: ReturnType<typeof getNetworkBuffer>;
};

function getLocalStorageInfo() {
  try {
    const keys = Object.keys(window.localStorage);
    let totalBytes = 0;
    for (const k of keys) {
      const v = window.localStorage.getItem(k) || "";
      totalBytes += k.length + v.length;
    }
    return { keys, size_kb: Math.round(totalBytes / 1024) };
  } catch {
    return { keys: [], size_kb: 0 };
  }
}

function getSessionStorageKeys() {
  try {
    return Object.keys(window.sessionStorage);
  } catch {
    return [];
  }
}

function getColorScheme(): "light" | "dark" | "unknown" {
  if (typeof window === "undefined" || !window.matchMedia) return "unknown";
  if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
  return "unknown";
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false;
  } catch {
    return false;
  }
}

export async function captureDebugSnapshot(opts: {
  active_team_id?: string | null;
}): Promise<DebugSnapshot> {
  const { data: sessionData } = await supabaseV2.auth.getSession().catch(() => ({ data: { session: null } as any }));
  const user = sessionData?.session?.user ?? null;
  const meta = (user?.user_metadata as Record<string, unknown> | null | undefined) || null;

  const ls = getLocalStorageInfo();
  const ss = getSessionStorageKeys();

  return {
    collected_at: new Date().toISOString(),
    app: {
      build_hash: (import.meta.env.VITE_BUILD_HASH as string | undefined) || null,
      build_time: (import.meta.env.VITE_BUILD_TIME as string | undefined) || null,
      mode: import.meta.env.MODE || null,
    },
    user: {
      id: user?.id || null,
      email: user?.email || null,
      lang: i18n.language || "—",
      meta_keys: meta ? Object.keys(meta) : [],
    },
    team: {
      active_id: opts.active_team_id || null,
    },
    view: {
      pathname: window.location.pathname,
      href: window.location.href,
      referrer: document.referrer || null,
    },
    browser: {
      user_agent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      platform: (navigator as any).platform,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${screen.width}x${screen.height}`,
      device_pixel_ratio: window.devicePixelRatio,
      online: navigator.onLine,
      cookies_enabled: navigator.cookieEnabled,
      color_scheme: getColorScheme(),
      reduced_motion: reducedMotion(),
    },
    storage: {
      localstorage_keys: ls.keys,
      localstorage_size_kb: ls.size_kb,
      sessionstorage_keys: ss,
    },
    push: {
      permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
    },
    console_tail: getConsoleBuffer(),
    network_tail: getNetworkBuffer(),
  };
}
